import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faRotateRight, faCalendarXmark, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import PostFormView from "../PostFormView";

const FailedSchedules = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editPost, setEditPost] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await crudApi.getAll(SocialEndpoints.posts.schedule.failed, setItems, "schedules");
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 0", color: "#9299a8" }}>
        <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2.5px dotted #f34e4e", animation: "dotted-spin 0.9s linear infinite" }} />
        {t("Backend.FailedSchedules.Loading")}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: "#9299a8" }}>
        <FontAwesomeIcon icon={faCalendarXmark} style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }} />
        <p style={{ margin: 0, fontSize: 14 }}>{t("Backend.FailedSchedules.Empty")}</p>
      </div>
    );
  }

  const openEdit = (item) => {
    const post = item.post ?? item;
    setEditPost(post);
    setShowEdit(true);
  };

  const deleteError = async (e, item) => {
    e.stopPropagation();
    const response = await crudApi.patch(SocialEndpoints.posts.schedule.errorDelete + item.id, {});
    if (response.ok) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((item, index) => (
        <div
          key={item.id ?? index}
          onClick={() => openEdit(item)}
          style={{
            display: "flex", alignItems: "flex-start", gap: 14,
            padding: "16px 20px", borderRadius: 12,
            background: "rgba(243,78,78,0.05)",
            border: "1px solid rgba(243,78,78,0.18)",
            borderLeft: "4px solid #f34e4e",
            cursor: "pointer", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(243,78,78,0.1)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(243,78,78,0.05)"; }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "rgba(243,78,78,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#f34e4e", fontSize: 16 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#1a2236" }}>
              {item.post?.title ?? item.title ?? `Schedule #${item.id}`}
            </p>
            {item.scheduled_at && (
              <p style={{ margin: "0 0 6px", fontSize: 12, color: "#9299a8" }}>
                {t("Backend.FailedSchedules.ScheduledFor")} {new Date(item.scheduled_at).toLocaleString()}
              </p>
            )}
            {item.error_message && (
              <p style={{
                margin: 0, fontSize: 12, color: "#c0001a",
                background: "rgba(243,78,78,0.08)", borderRadius: 6,
                padding: "6px 10px", lineHeight: 1.5,
              }}>
                {item.error_message}
              </p>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
            {item.platform && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.8px",
                textTransform: "uppercase", color: "#9299a8",
              }}>
                {item.platform}
              </span>
            )}
            <button
              onClick={(e) => deleteError(e, item)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 6,
                border: "1px solid rgba(243,78,78,0.35)",
                background: "transparent", color: "#f34e4e",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(243,78,78,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <FontAwesomeIcon icon={faCircleXmark} style={{ fontSize: 11 }} />
              {t("Backend.FailedSchedules.DeleteError", "Delete error")}
            </button>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setLoading(true);
            crudApi.getAll(SocialEndpoints.posts.schedule.failed, setItems, "schedules")
              .finally(() => setLoading(false));
          }}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e5ea",
            background: "#fff", color: "#374151", fontSize: 13, cursor: "pointer",
          }}
        >
          <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 12 }} />
          {t("Common.Refresh")}
        </button>
      </div>
      <PostFormView
        showEdit={showEdit}
        setShowEdit={setShowEdit}
        editData={editPost}
        setEditData={setEditPost}
        dataList={items}
        setDataList={setItems}
      />
    </div>
  );
};

export default FailedSchedules;
